import { useContext, useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import AccionaFormItem from '../../../../../components/AccionaFormItem';
import AccionaInput from '../../../../../components/AccionaInput';
import Device from '../../../../../models/thermalControl/Device';
import style from './index.module.scss';
import AccionaButton from '../../../../../components/AccionaButton';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { MainContext } from '../../../../../context/MainContext';
import AccionaAlert from '../../../../../components/AccionaAlert';
import { checkDeviceExists } from '../../../../../services/api/thermalControl/devices';

const {
  wrapper,
  footer,
  identification,
  main,
  showAddButton,
  error,
  hiddenZone,
  visible,
} = style;

const CreateDevice = ({ initialData, onFinish, onCancel }) => {
  const intl = useIntl();
  const { project, setLoading: setMainLoading } = useContext(MainContext);
  const { errorCreated } = useSelector(
    ({ thermalControlDevices }) => thermalControlDevices,
  );
  const [device, setDevice] = useState(
    initialData || new Device({ project: project.id_project }),
  );
  const [isDeviceIdValidated, setIsDeviceIdValidated] = useState(
    initialData || false,
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const onAddClick = () => {
    const parsedDevice = new Device({
      ...device,
    });
    onFinish(parsedDevice);
  };

  const searchDevice = async () => {
    try {
      setLoading(true);
      const response = await checkDeviceExists(device.reference_number);
      const { data } = response;
      if (response.status === 200) {
        setIsDeviceIdValidated(true);
        setDevice((prev) => ({ ...prev, id: data.deviceId }));
      }
      setLoading(false);
    } catch ({ response }) {
      if (response?.status === 409) {
        setErrorMessage(
          'strength.thermalcontrol.devices.create.id.409.validation',
        );
      } else if (response?.status === 410) {
        setErrorMessage(
          'strength.thermalcontrol.devices.create.id.410.validation',
        );
      } else {
        setErrorMessage(
          'strength.thermalcontrol.devices.create.reference.validation',
        );
      }
      setIsDeviceIdValidated(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorCreated) {
      setMainLoading(false);
    }
  }, [setMainLoading, errorCreated]);

  return (
    <div className={wrapper}>
      <div className={main}>
        <div className={identification}>
          <div>
            <AccionaFormItem>
              <AccionaInput
                state={{ value: device.reference_number, status: [] }}
                label="reference_number"
                onChange={(label, reference) => {
                  setIsDeviceIdValidated(false);
                  setErrorMessage(undefined);
                  setDevice((prev) => ({
                    ...prev,
                    reference_number: reference,
                  }));
                }}
                disabled={loading || Boolean(initialData)}
              />
            </AccionaFormItem>
            <AccionaButton
              classIcon="icon fas fa-search"
              loading={loading}
              onClick={searchDevice}
              disabled={Boolean(!device.reference_number || initialData)}
            />
          </div>
          {errorMessage && (
            <p className={error}>{intl.formatMessage({ id: errorMessage })}</p>
          )}
        </div>
        <div className={`${hiddenZone} ${isDeviceIdValidated ? visible : ''}`}>
          <AccionaFormItem>
            <AccionaInput
              state={{ value: device.description, status: [] }}
              label="description"
              onChange={(label, description) => {
                setDevice((prev) => ({ ...prev, description }));
              }}
            />
          </AccionaFormItem>
        </div>
        {errorCreated && (
          <AccionaAlert
            message={intl.formatMessage({
              id: 'strength.thermalcontrol.devices.create.error',
            })}
          />
        )}
      </div>
      <div className={`${footer} ${isDeviceIdValidated ? showAddButton : ''}`}>
        <AccionaButton
          type="default"
          msg={intl.formatMessage({ id: 'cancel' })}
          onClick={onCancel}
        />
        {isDeviceIdValidated && (
          <AccionaButton
            msg={intl.formatMessage({
              id: initialData ? 'button.update.end' : 'button.add',
            })}
            onClick={onAddClick}
          />
        )}
      </div>
    </div>
  );
};

CreateDevice.propTypes = {
  initialData: PropTypes.instanceOf(Device),
  onFinish: PropTypes.func,
  onCancel: PropTypes.func,
};

CreateDevice.defaultProps = {
  initialData: undefined,
  onFinish: () => {},
  onCancel: () => {},
};

export default CreateDevice;
